import { formatDate } from "@angular/common";
import {
  Component,
  EventEmitter,
  Inject,
  Input,
  LOCALE_ID,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import {
  Field,
  FormComponent,
  FormResult,
  FormReturnType,
  NaturalFormConfig,
  NaturalFormFlavour,
  NaturalFormStyle,
  Section,
} from "@cotecna/dynamic-view-engine";
import {
  CLIENT,
  COMDIV,
  EXTERNAL_ID,
  FALCON,
} from "src/app/inspection/constants/constants";
import { GeneralInfo } from "src/app/inspection/model/general-info";
import {
  APPLICATION_ORIGIN_FIELD_ID,
  CLIENT_FIELD_ID,
  DATE_FIELD_ID,
  DATE_FIELD_PROPERTY_NAME,
  EXTERNAL_FIELD_ID,
  GENERAL_INFO_CONTROLS,
  GENERAL_INFO_FORM,
  INSPECTION_TYPE_FIELD_ID,
} from "src/app/inspection/static-forms/general-info-form";
import { AdditionalFieldControlType } from "src/app/settings/models/preset";
import { UserRolePermissionsService } from "src/app/shared/services/user-role-permissions/user-role-permissions.service";

import { GeneralInfoService } from "../../../services/general-info.service";

@Component({
  selector: "im-general-info-editable-mode",
  templateUrl: "./general-info-editable-mode.component.html",
  styleUrls: ["./general-info-editable-mode.component.scss"],
  standalone: false,
})
export class GeneralInfoEditableModeComponent implements OnInit {
  @ViewChild("naturalForm")
  public form: FormComponent;

  @Input()
  public inspectionId: string = "";

  @Input()
  public generalInfo: GeneralInfo = null;

  @Output()
  public generalInfoSaved: EventEmitter<GeneralInfo> = new EventEmitter();

  @Output()
  public cancelEdition: EventEmitter<void> = new EventEmitter();

  public sections: Section[] = [];
  public controls: any[] = [];
  public otherFields: Field[] = [];
  private readonly DATE_FORMAT: string = "yyyy-MM-ddTHH:mm";

  public naturalFormConfig: NaturalFormConfig = {
    displayMode: NaturalFormStyle.material,
    flavor: NaturalFormFlavour.informative,
    notifyFieldChange: true,
    returnType: FormReturnType.entity,
    readonly: false,
  };

  constructor(
    @Inject(LOCALE_ID) public locale: string,
    public generalInfoService: GeneralInfoService,
    private userRolePermissionsService: UserRolePermissionsService,
  ) {}

  ngOnInit() {
    this.fillInformation();
    this.manageComdivInspection();
    this.manageFalconInspection();
  }

  public onFormSubmitted(formResult: FormResult) {
    if (formResult.isValid) {
      const data = this.prepareGeneralInfoDataToSend(formResult.data);
      setTimeout(() => this.generalInfoSaved.emit(data));
    }
  }

  private prepareGeneralInfoDataToSend(formData: any): GeneralInfo {
    const generalInfoToSend = { ...formData };

    //manage additional fields
    generalInfoToSend["additionalFields"] = [];
    const additionalFields = this.generalInfoService.getOthersComponents();
    for (const field of additionalFields) {
      generalInfoToSend["additionalFields"].push({
        id: field.instance?.field.id,
        name: field.instance?.field.label,
        value: field.instance?.field.value,
        controlType: field.instance?.controlType,
        selectionOptions:
          field.instance?.controlType == AdditionalFieldControlType.Selection
            ? field.instance?.field.readableValue
            : [],
        mandatory: field.instance?.mandatory,
      });
    }
    //manage dates
    generalInfoToSend.date = formData[DATE_FIELD_PROPERTY_NAME].split(",").map(
      (date) => new Date(date).toUTCString(),
    );
    //manage client
    generalInfoToSend.client = this.sections[0]?.fields.filter(
      (field) => field.id == CLIENT_FIELD_ID,
    )[0]?.readableValue;

    return generalInfoToSend as GeneralInfo;
  }

  public cancel() {
    this.cancelEdition.next();
  }

  public ValidateKeyCode(event: any) {
    this.generalInfoService.ValidateKey(event);
  }

  private fillInformation() {
    this.sections = JSON.parse(JSON.stringify(GENERAL_INFO_FORM));
    this.controls = JSON.parse(JSON.stringify(GENERAL_INFO_CONTROLS));

    if (this.userRolePermissionsService.isServiceProvider()) {
      this.sections[0].fields.find((f) => f.id == CLIENT_FIELD_ID)!.readOnly =
        true;
      this.sections[0].fields.find((f) => f.id == EXTERNAL_FIELD_ID)!.readOnly =
        true;
    }

    if (this.generalInfo) {
      this.sections[0].fields.forEach((field) => {
        this.fillGeneralInfoField(field);
      });
    }
  }

  private manageComdivInspection(): void {
    const applicationOfOrigin = this.sections[0].fields.find(
      (field) => field.propertyName === APPLICATION_ORIGIN_FIELD_ID,
    )?.value;

    if (applicationOfOrigin?.toLowerCase() == COMDIV) {
      this.sections[0].fields.forEach((f) => {
        if (f.propertyName == EXTERNAL_ID || f.propertyName == CLIENT)
          f.readOnly = true;
      });
    }
  }

  private manageFalconInspection(): void {
    const applicationOfOrigin = this.sections[0].fields.find(
      (field) => field.propertyName === APPLICATION_ORIGIN_FIELD_ID,
    )?.value;

    if (applicationOfOrigin?.toLowerCase() == FALCON) {
      this.sections[0].fields.forEach((f) => {
        if (f.propertyName == EXTERNAL_ID || f.propertyName == CLIENT)
          f.readOnly = true;
      });
    }
  }

  private fillGeneralInfoField(field: Field): void {
    switch (field.propertyName) {
      case INSPECTION_TYPE_FIELD_ID:
        field.visible = false;
        break;
      case APPLICATION_ORIGIN_FIELD_ID:
        field.value = this.generalInfo.originApplicationName;
        break;
      case DATE_FIELD_PROPERTY_NAME:
        field.value = this.handleDates();
        break;
      case CLIENT_FIELD_ID:
        field.readableValue = this.generalInfo.client;
        field.value = this.generalInfo.client;
        break;
      default:
        field.value = this.generalInfo[field.propertyName];
    }
    if (field.value) field.valid = true;
  }

  private handleDates(): string {
    const dates = this.generalInfo[DATE_FIELD_ID] as Date[];
    return dates
      .map((date) => formatDate(new Date(date), this.DATE_FORMAT, this.locale))
      .join(",");
  }
}
